/* cSpell:ignore libphonenumber */

import React, { useCallback } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import {
  enforceNoFractionalLigatures,
  formatPhoneNumber,
} from '@sweetgreen/garnish';

import type { TranslationKeys } from './locales';
import { enUs, es } from './locales';

export type FormatXMLElementFn<
  T,
  R = string | T | ReadonlyArray<string | T>,
> = (parts: ReadonlyArray<string | T>) => R;
export type PrimitiveType = string | number | boolean | null | undefined | Date;
export type TranslationValues = Record<
  string,
  PrimitiveType | FormatXMLElementFn<string, string>
>;
export type LocaleNames = 'en-US' | 'es';
export type Locale = {
  readonly label: LocaleNames;
  readonly translation: typeof enUs | typeof es;
};

// NOTE: Purposely separating currency from locale since
// locale should only govern which translation should be used
// and how the values are formatted, but NOT the currency with
// which the transaction is made.
export type SupportedCurrencies = 'USD'; // We only support USD for the time being
export type SupportedLocales = Readonly<Record<string, Locale>>;

export const supportedLocales: SupportedLocales = {
  enUs: {
    label: 'en-US',
    translation: enUs,
  },
  es: {
    label: 'es',
    translation: es,
  },
};

export type LocalizationProviderInterface = {
  readonly children: React.ReactElement;
};

export type LocalizationContextInterface = {
  readonly setCurrentLocale: React.Dispatch<React.SetStateAction<Locale>>;
  readonly currentLocale: Locale;
};

export const LocalizationContext =
  React.createContext<LocalizationContextInterface>({
    currentLocale: supportedLocales.enUs,
    setCurrentLocale: () => null,
  });

export const useLocalizationContext = () => {
  const { currentLocale, setCurrentLocale } =
    React.useContext(LocalizationContext);
  const intl = useIntl();

  const formatMessage = useCallback(
    (key: TranslationKeys, values?: TranslationValues): string =>
      String(
        intl.formatMessage(
          {
            id: key,
            defaultMessage: 'Missing Translation',
          },
          values,
        ),
      ),
    [intl],
  );

  type FormatCurrencyParameter = Parameters<typeof intl.formatNumber>[0];
  type FormatCurrencyReturn = ReturnType<typeof intl.formatNumber>[0];
  const formatPrice = (
    value: FormatCurrencyParameter,
    currency: SupportedCurrencies,
  ): FormatCurrencyReturn =>
    String(
      intl.formatNumber(Number(value) / 100, {
        style: 'currency',
        currency: String(currency),
      }),
    );

  const formatDate = (date: string | Date) => {
    return enforceNoFractionalLigatures(intl.formatDate(date));
  };

  return {
    currentLocale,
    formatPlural: intl.formatPlural,
    formatPrice,
    formatDate,
    formatMessage,
    t: formatMessage, // Alias for formatMessage
    formatNumber: intl.formatNumber,
    formatPhoneNumber,
    setCurrentLocale,
  };
};

export const LocalizationProvider = ({
  children,
}: LocalizationProviderInterface) => {
  const [currentLocale, setCurrentLocale] = React.useState<Locale>(
    supportedLocales.enUs,
  );

  const value = React.useMemo(
    () => ({
      setCurrentLocale,
      currentLocale,
    }),
    [currentLocale],
  );

  return (
    <LocalizationContext.Provider value={value}>
      <IntlProvider
        messages={currentLocale.translation}
        locale={currentLocale.label}
        defaultLocale={supportedLocales.enUs.label}
      >
        {children}
      </IntlProvider>
    </LocalizationContext.Provider>
  );
};
